import type {
  DisplayValueParams,
  EchoParams,
  LiveQuantityParams,
  ReadTypedParams,
  TypedReading,
  WholeFractionDigitsParams
} from '@/domains/measurement-units/utils/stock-quantity-input/stockQuantityInput.types'
import type { DisplayUnit } from '@/utils/unit-util/UnitUtil.types'

import { formatStock, fromBase, toBaseCount, unitLabel } from '@/utils/unit-util/unitUtil'

/** A base count as the figure the field shows in `unit`. */
const displayValueOf = ({ baseCount, unit }: DisplayValueParams): number | null => {
  if (baseCount === null) return null

  return unit ? fromBase({ baseCount, unit }) : baseCount
}

/** The refusal for a figure finer than the base unit. Names the unit only when it is known. */
const notWholeMessage = (baseUnit?: DisplayUnit): string =>
  baseUnit ? `Not a whole number of ${unitLabel({ unit: baseUnit, quantity: 2 })}` : 'Not a whole number'

/** What a typed value stores, or why it is refused. An empty field clears the model. */
const readTyped = ({ quantity, unit, baseUnit }: ReadTypedParams): TypedReading => {
  if (quantity === null) return { baseCount: null, refusal: null }

  const baseCount = toBaseCount({ quantity, unit })

  if (baseCount !== null) return { baseCount, refusal: null }

  return { baseCount: null, refusal: notWholeMessage(baseUnit) }
}

/** "= 4.82 kg" — the stored figure in the stock unit. Nothing until a value is held. */
const echoOf = ({ baseCount, stockUnit }: EchoParams): string | null =>
  baseCount === null ? null : `= ${formatStock({ baseCount, unit: stockUnit })}`

/**
 * The most decimals `unit` can take while every figure stays a whole number of base units: the largest d
 * up to its precision where 10 ^ d divides its factor. kg (1,000, 3) takes 3; dag (10, 2) takes 1, since
 * 1.25 dag is 12.5 g; g takes 0. A unit that never converts, or none, takes whole figures.
 */
const wholeFractionDigits = ({ unit }: WholeFractionDigitsParams): number => {
  const factor = unit?.conversion_factor

  if (!unit || !factor) return 0

  let digits = unit.precision

  while (digits > 0 && factor % 10 ** digits !== 0) digits -= 1

  return digits
}

/**
 * The count a screen reads while the merchant types: the typed one, else the committed one.
 *
 * `TwnStockQuantityInput` commits `v-model` on blur but emits `typing` on each keystroke. A preview, a refusal
 * or a button label reads this, so it agrees with the field. A save reads the committed count alone. A blank
 * field typed over a committed count is null, not the committed count.
 */
const liveQuantityOf = ({ typed, committed }: LiveQuantityParams): number | null =>
  typed === undefined ? committed : typed

export { displayValueOf, echoOf, liveQuantityOf, notWholeMessage, readTyped, wholeFractionDigits }
